/**
 * TestEdit Component (Rebuilt)
 *
 * Clean, modern implementation for editing daily exercises (tests).
 * Features:
 * - Test metadata editing (name, type, summary, description, asset)
 * - Question management with tabbed interface
 * - Support for all 9 question types including COMPREHENSION
 * - Program and difficulty level selection
 * - Media upload management
 * - Form validation before save
 * - High performance with React.memo and optimized re-renders
 */

import React, {useMemo} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {Button, Collapse, Modal, Spin, Tabs, Typography} from 'antd';
import {DailyExerciseMetadataForm} from './components/DailyExerciseMetadataForm';
import {QuestionTabPanel} from './components/QuestionTabPanel';
import {
  DailyExerciseStatusTag,
  DailyExerciseHistory,
} from '../DailyExercisesReview';
import {useDailyExerciseData} from './hooks/useDailyExerciseData';
import {useComprehensionLogic} from './hooks/useComprehensionLogic';
import {useQuestionValidation} from './hooks/useQuestionValidation';
import type {QuestionWithNewFields} from '../Questions/QuestionTypes';

const {Title} = Typography;
const {TabPane} = Tabs;

/**
 * Main TestEdit component
 */
export const TestEdit: React.FC = () => {
  const history = useHistory();
  const params: {testId: string} = useParams();

  // Data management hook
  const {
    testName,
    setTestName,
    testType,
    setTestType,
    testDescription,
    setTestDescription,
    testSummary,
    setTestSummary,
    testAsset,
    setTestAsset,
    thumbnail,
    media,
    questions,
    setQuestions,
    activeQuestionIndex,
    setActiveQuestionIndex,
    addNewQuestion,
    deleteQuestion,
    programsList,
    isLoading,
    isSaving,
    saveTest,
    originalTest,
  } = useDailyExerciseData(params.testId);

  // Comprehension logic hook (using payload.blanks structure)
  const {
    selectedBlankIndex,
    setSelectedBlankIndex,
    handleTextChange,
    handleTextBlur,
    handleCorrectAnswerChange,
    handleChoiceValueChange,
    handleAddChoice,
  } = useComprehensionLogic();

  // Validation hook
  const {validateAllQuestions, validateTestMetadata} = useQuestionValidation();

  /**
   * Handles tab change (switch between questions)
   */
  const handleTabChange = (activeKey: string) => {
    const index = parseInt(activeKey);
    setActiveQuestionIndex(index);

    // Reset selected blank index for COMPREHENSION questions (always start with first blank)
    const question = questions[index];
    if (
      question &&
      question.type === 'COMPREHENSION' &&
      question.payload?.blanks?.length
    ) {
      setSelectedBlankIndex(0);
    }
  };

  /**
   * Handles tab edit action (add new question)
   */
  const handleTabEdit = (targetKey: any, action: 'add' | 'remove') => {
    if (action === 'add') {
      addNewQuestion();
    }
  };

  /**
   * Updates a specific question in the questions array
   */
  const handleQuestionUpdate = (updatedQuestion: QuestionWithNewFields) => {
    const updatedQuestions = [...questions];
    updatedQuestions[activeQuestionIndex] = updatedQuestion;
    setQuestions(updatedQuestions);
  };

  /**
   * Handles question deletion
   */
  const handleDeleteQuestion = async () => {
    await deleteQuestion(activeQuestionIndex);
  };

  /**
   * Handles text change for current question
   */
  const handleQuestionTextChange = (
    newText: string,
    isAIText: boolean = false,
  ) => {
    const updatedQuestions = handleTextChange(
      activeQuestionIndex,
      newText,
      questions,
      isAIText,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles text blur for current question
   */
  const handleQuestionTextBlur = () => {
    const updatedQuestions = handleTextBlur(activeQuestionIndex, questions);
    setQuestions(updatedQuestions);
  };

  /**
   * Handles COMPREHENSION choice value change (using payload.blanks structure)
   */
  const handleComprehensionChoiceChange = (
    blankIndex: number,
    choiceIndex: number,
    value: string,
  ) => {
    const updatedQuestions = handleChoiceValueChange(
      activeQuestionIndex,
      blankIndex,
      choiceIndex,
      value,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles COMPREHENSION correct answer change (correctAnswer is a string, not an index)
   */
  const handleComprehensionCorrectAnswer = (
    blankIndex: number,
    correctAnswer: string,
  ) => {
    const updatedQuestions = handleCorrectAnswerChange(
      activeQuestionIndex,
      blankIndex,
      correctAnswer,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles adding a new choice to COMPREHENSION question blank
   */
  const handleComprehensionAddNewChoice = (blankIndex: number) => {
    const updatedQuestions = handleAddChoice(
      activeQuestionIndex,
      blankIndex,
      questions,
    );
    setQuestions(updatedQuestions);
  };

  /**
   * Handles save button click with validation
   */
  const handleSave = async () => {
    try {
      // Validate test metadata
      const metadataValidation = validateTestMetadata(
        testName,
        testType,
        testDescription,
        testSummary,
      );

      if (!metadataValidation.isValid) {
        window.alert(
          'Test metadata validation failed:\n' +
            metadataValidation.errors.join('\n'),
        );
        return;
      }

      // Validate all questions
      const questionsValidation = validateAllQuestions(questions);

      if (!questionsValidation.isValid) {
        window.alert(
          'Question validation failed:\n' +
            questionsValidation.errors.join('\n'),
        );
        return;
      }

      // Save test
      await saveTest();

      // Navigate back to tests list
      history.push('/tests');
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  // Memoize current question to prevent unnecessary re-renders
  const currentQuestion = useMemo(
    () => questions[activeQuestionIndex],
    [questions, activeQuestionIndex],
  );

  return (
    <>
      <div style={{padding: 10, width: '100vw'}}>
        <Title style={{textAlign: 'center'}}>Edit Daily Exercise</Title>

        {/* Approval status & modification history (read-only) */}
        <div style={{marginBottom: 16}}>
          <span style={{fontWeight: 'bold', marginRight: 8}}>Status:</span>
          <DailyExerciseStatusTag status={(originalTest as any)?.status} />
          {(originalTest as any)?.createdBy && (
            <span style={{marginLeft: 16}}>
              <span style={{fontWeight: 'bold'}}>Created by: </span>
              {(originalTest as any)?.createdBy}
            </span>
          )}
          {(originalTest as any)?.rejectionReason && (
            <div style={{marginTop: 4}}>
              <span style={{fontWeight: 'bold'}}>Rejection reason: </span>
              {(originalTest as any)?.rejectionReason}
            </div>
          )}
          <Collapse style={{marginTop: 8}} ghost>
            <Collapse.Panel header="Modification history" key="history">
              <DailyExerciseHistory history={(originalTest as any)?.history} />
            </Collapse.Panel>
          </Collapse>
        </div>

        {/* Test Metadata Form */}
        <DailyExerciseMetadataForm
          testName={testName}
          questionCount={questions.length}
          onTestNameChange={setTestName}
          testDescription={testDescription}
          onTestDescriptionChange={setTestDescription}
          testSummary={testSummary}
          onTestSummaryChange={setTestSummary}
          testAsset={testAsset}
          onTestAssetChange={setTestAsset}
          thumbnail={thumbnail}
          media={media}
        />

        {/* Questions Tabs */}
        <Tabs
          type="editable-card"
          onChange={handleTabChange}
          activeKey={activeQuestionIndex.toString()}
          onEdit={handleTabEdit}>
          {questions?.map((question, index) => (
            <TabPane tab={`question_${index}`} key={index} closable={false}>
              {/* Only render the active tab's content for performance */}
              {index === activeQuestionIndex && (
                <QuestionTabPanel
                  question={question}
                  questionIndex={index}
                  testType={testType}
                  programsList={programsList}
                  onQuestionUpdate={handleQuestionUpdate}
                  onDeleteQuestion={handleDeleteQuestion}
                  selectedBlankIndex={selectedBlankIndex}
                  onSelectedBlankIndexChange={setSelectedBlankIndex}
                  onTextChange={handleQuestionTextChange}
                  onTextBlur={handleQuestionTextBlur}
                  onComprehensionChoiceValueChange={
                    handleComprehensionChoiceChange
                  }
                  onComprehensionCorrectAnswerChange={
                    handleComprehensionCorrectAnswer
                  }
                  onComprehensionAddChoice={handleComprehensionAddNewChoice}
                  isDeleting={isSaving}
                />
              )}
            </TabPane>
          ))}
        </Tabs>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          loading={isSaving}
          disabled={isLoading || isSaving}
          style={{marginTop: '20px'}}>
          {isSaving ? 'Saving...' : 'Save Daily Exercise'}
        </Button>
      </div>

      {/* Loading Modal */}
      <Modal
        okButtonProps={{hidden: true}}
        cancelButtonProps={{hidden: true}}
        visible={isLoading}
        closable={true}>
        <div>
          <Spin size="large" style={{margin: 'auto', width: '100%'}} />
          <p style={{textAlign: 'center'}}>Loading...</p>
        </div>
      </Modal>
    </>
  );
};
